import { createSelector } from "reselect";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { selectIsNewOnlyEnabled } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import type { AppsSearchTableState } from "app/pages/sales-intelligence/modules/apps-search/store/types";
import {
    type ILeadsSearchFiltersDto,
    selectFiltersValue,
    selectFiltersValueDeepEqual,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { selectDirtyFiltersFlag } from "app/pages/sales-intelligence/modules/leads-search-filters/store/selectors";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { selectContactsSearchText } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

import type { ExpandedRowVariant } from "../types/expanded-row";
import type { TableRowKey } from "../types/table";
import { isRowExpanded } from "../utils/expanded-row";
import { getLeadDomainFromRow } from "../utils/rows";

import type { LeadsSearchState } from "./types";
import { createLeadDomainsSearchRequestDto, createSearchResultsDtoByLeadsType } from "./utils";
import { createCrmRecordsRequestDto } from "./utils/selectCombiners";

const selectLeadsSearchState = createStatePropertySelector<LeadsSearchState>(
    selectSalesIntelligenceState("leadsSearch"),
);

export const selectActiveLeadsType = selectLeadsSearchState("activeLeadsType");

const selectTabs = selectLeadsSearchState("tabs");

export const selectExpandedRowState = selectLeadsSearchState("expandedRowState");

export const selectExpandedRowKeys = selectLeadsSearchState("expandedRowKeys");

export const selectExpandedRowVariant = selectLeadsSearchState("expandedRowVariant");

export const selectIsRowExpanded = (
    state: RootState,
    key: TableRowKey | undefined,
    variant: ExpandedRowVariant,
): boolean => isRowExpanded(selectExpandedRowKeys(state), selectExpandedRowVariant(state), key, variant);

export const selectColumnNamesForHighlight = selectLeadsSearchState("columnNamesForHighlight");

export const selectIsRowSelectionEnabled = selectLeadsSearchState("isRowSelectionEnabled");

const selectSelectedRows = selectLeadsSearchState("selectedRows");

export const selectSearchText = selectLeadsSearchState("searchText");

export const selectIsAggregatedDataUsed = selectLeadsSearchState("isAggregatedDataUsed");

export const selectIsShowSavedSearchListPanel = selectLeadsSearchState("isShowSavedSearchListPanel");

export const selectHighlightedRowDomains = selectLeadsSearchState("highlightedRowDomains");

export const selectIsTableRowExpanded = createSelector(
    [selectExpandedRowKeys],
    (expandedRowKeys) => expandedRowKeys.length > 0,
);

export const selectSelectedRowKeys = createSelector([selectSelectedRows], (selectedRows) =>
    selectedRows.map((row) => row.key).filter((key) => key !== undefined),
);

export const selectSelectedRowDomains = createSelector([selectSelectedRows], (selectedRows) =>
    selectedRows.map(getLeadDomainFromRow),
);

const selectListsFiltersValue = createSelector(
    [selectFiltersValueDeepEqual],
    (queryFilters: Partial<ILeadsSearchFiltersDto>) => {
        const contactOnlyFiltersKeys = ["companyDomains"];

        return {
            ...queryFilters,
            contactsFilters: Object.fromEntries(
                Object.entries(queryFilters.contactsFilters || {}).filter(
                    ([key]) => !contactOnlyFiltersKeys.includes(key),
                ),
            ),
        };
    },
);

const selectAppsSearchText = createStatePropertySelector<AppsSearchTableState>(
    selectSalesIntelligenceState("appsSearch"),
)("searchText");

export const selectSearchResultsDtoByLeadsType = createSelector(
    [
        selectTabs,
        selectSearchText,
        selectContactsSearchText,
        selectIsNewOnlyEnabled,
        selectFiltersValueDeepEqual,
        selectIsAggregatedDataUsed,
        selectAppsSearchText,
    ],
    createSearchResultsDtoByLeadsType,
);
export const selectListsSearchResultsDtoByLeadsType = createSelector(
    [
        selectTabs,
        selectSearchText,
        selectContactsSearchText,
        selectIsNewOnlyEnabled,
        selectListsFiltersValue,
        selectIsAggregatedDataUsed,
        selectAppsSearchText,
    ],
    createSearchResultsDtoByLeadsType,
);

export const selectIsLeadsSearchDtoReady = (state: RootState): boolean => selectDirtyFiltersFlag(state);

export const selectActiveSearchResultsDto = createSelector(
    [selectActiveLeadsType, selectSearchResultsDtoByLeadsType],
    (activeLeadsType, dtoByLeadsType) => dtoByLeadsType[activeLeadsType],
);

export const selectDomainSearchResultsDto = createSelector(
    [selectExpandedRowState, selectFiltersValue, selectIsAggregatedDataUsed],
    ({ page, pageSize, asc, orderBy }, queryFilters, leadGenTrafficAggregationEnabled) =>
        createLeadDomainsSearchRequestDto({
            page,
            pageSize,
            asc,
            orderBy,
            queryFilters,
            leadGenTrafficAggregationEnabled,
        }),
);

export const selectCrmRecordsRequestDto = createSelector(
    [selectExpandedRowState, selectFiltersValue, (_state: RootState, provider: CrmIntegrationProvider) => provider],
    createCrmRecordsRequestDto,
);
